/**
 * The page the browser lands on after a Google sign-in started from a desktop app.
 *
 * One job: get the result to the app. When the app takes it, this page is never seen,
 * the browser navigates away to the app's scheme within a moment of arriving. It exists
 * because that step cannot be skipped: an `https` address on our own domain is something
 * every browser will follow, and `orobasagent://` is something each one handles
 * differently.
 *
 * The decision itself lives in `desktopHandOff.ts`, and this file is the rendering of it.
 *
 * ## The one address that never carries a language prefix
 *
 * The apps put `/auth/callback` in their OAuth `redirect_to`, so the browser arrives
 * here at an unprefixed path and this page is Portuguese. That is deliberate and it is
 * the reason the unprefixed path stays a working Portuguese path in `i18n/locale.ts`:
 * a released app build cannot be asked to send people to `/en/auth/callback`.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';

import { format, intlLocale, useI18n } from '../i18n';
import { Link } from '../router';
import { readCallback, type Outcome } from '../auth/desktopHandOff';

/**
 * How long to wait before offering the button.
 *
 * Long enough that a machine where the scheme opens immediately has already left, and
 * short enough that someone whose browser blocked it is not left staring at a spinner
 * they cannot explain. The button is what makes the wait survivable: a redirect that
 * silently fails is indistinguishable from the page being broken.
 */
const AUTO_HAND_OFF_MS = 1200;

export const DesktopCallback: React.FC = () => {
  const outcome = useMemo(
    () => readCallback(new URLSearchParams(window.location.search)),
    [],
  );
  const { t } = useI18n();

  // `null` means "not yet given up", which is different from "no hand-off to make" and
  // different from "here is the button". Three states, not a flag.
  const [offered, setOffered] = useState<boolean>(() => outcome.kind !== 'handing-off');

  useEffect(() => {
    if (outcome.kind !== 'handing-off') return;

    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (!cancelled) setOffered(true);
    }, AUTO_HAND_OFF_MS);

    window.location.href = outcome.url;

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [outcome]);

  return (
    <section className="mx-auto flex min-h-[70vh] w-full max-w-md flex-col items-center justify-center px-5 text-center">
      <img src="/OrobasWorkLogoNewGold.svg" alt={t.callback.crestAlt} width={40} height={40} className="block mb-6" />

      {outcome.kind === 'handing-off' && !offered ? (
        <>
          <Loader2 className="size-6 animate-spin text-gold" aria-hidden="true" />
          <h1 className="mt-5 text-lg font-semibold text-cream">
            {format(t.callback.openingTitle, { app: outcome.app.name })}
          </h1>
          <p className="mt-2 text-sm text-muted">{t.callback.openingBody}</p>
        </>
      ) : (
        <Body outcome={outcome} />
      )}
    </section>
  );
};

const Body: React.FC<{ outcome: Outcome }> = ({ outcome }) => {
  const { locale, t } = useI18n();

  switch (outcome.kind) {
    case 'handing-off':
      // The automatic hand-off either already worked, in which case the browser has
      // left and this is never seen, or the browser refused the scheme. So the copy
      // names the cause rather than repeating the spinner.
      return (
        <>
          <h1 className="text-lg font-semibold text-cream">
            {format(t.callback.blockedTitle, { app: outcome.app.name })}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-muted">
            {format(t.callback.blockedBody, { app: outcome.app.name })}
          </p>
          <a
            href={outcome.url}
            className="pill pressable mt-6 inline-flex items-center gap-2 bg-gold px-6 py-3 text-ink hover:bg-gold-bright"
          >
            {format(t.callback.openAppCta, { app: outcome.app.name })}
            <ArrowRight className="size-4" aria-hidden="true" />
          </a>
        </>
      );

    case 'declined':
      return (
        <>
          <h1 className="text-lg font-semibold text-cream">{t.callback.declinedTitle}</h1>
          <p className="mt-2 text-sm leading-relaxed text-muted">
            {/*
              Picked here, at render, rather than resolved into the outcome when the
              query was read. A sentence resolved at read time is a sentence frozen in
              whichever language happened to be active, and it would sit under a
              heading in a language the reader did not ask for.
            */}
            {outcome.refusal === 'cancelled'
              ? t.callback.declinedCancelled
              : (outcome.providerReason ?? t.callback.declinedGeneric)}
          </p>
          <Link to="conta" className="pill pressable mt-6 bg-gold px-6 py-3 text-ink hover:bg-gold-bright">
            {t.callback.signInHere}
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </>
      );

    case 'unknown-app':
      return (
        <>
          <h1 className="text-lg font-semibold text-cream">{t.callback.unknownTitle}</h1>
          <p className="mt-2 text-sm leading-relaxed text-muted">{t.callback.unknownBody}</p>
          <Link to="conta" className="pill pressable mt-6 bg-gold px-6 py-3 text-ink hover:bg-gold-bright">
            {t.callback.signInHere}
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </>
      );

    case 'incomplete':
      return (
        <>
          <h1 className="text-lg font-semibold text-cream">{t.callback.incompleteTitle}</h1>
          <p className="mt-2 text-sm leading-relaxed text-muted">
            {/*
              Joined by `Intl.ListFormat`, which is the one place in this file that
              knows how a language writes a list. A hardcoded `' e '` produced
              "Faltou code e nonce" in English and Spanish; the conjunction is the
              part that does not survive translation.
            */}
            {format(t.callback.incompleteBody, {
              missing: new Intl.ListFormat(intlLocale(locale), {
                style: 'long',
                type: 'conjunction',
              }).format(outcome.missing),
            })}
          </p>
          <Link to="conta" className="pill pressable mt-6 bg-gold px-6 py-3 text-ink hover:bg-gold-bright">
            {t.callback.signInHere}
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </>
      );
  }
};